<template>
  <div
    class="dropdown-header"
    :class="{
      'dropdown-header__focus': isOpen && hasSearch,
      'dropdown-header__disabled': disabled,
      'dropdown-header__open': isOpen && !hasSearch
    }"
    :tabindex="disabled || hasSearch ? -1 : 0"
    @click="onClickHeader"
  >
    <IconSearch v-if="isOpen && hasSearch" class="dropdown-header__icon" />
    <input
      v-if="hasSearch"
      ref="inputRef"
      class="dropdown-header__input"
      :class="{ 'dropdown-header__disabled': disabled }"
      :placeholder="!isOpen ? title : `${$t('general__text__search')} ${searchType}`"
      :value="value"
      :disabled="disabled"
      :tabindex="disabled ? -1 : 0"
      @input="onChange"
      @click="onClickInput"
    />
    <div v-if="!hasSearch">
      <p class="dropdown-header__select" :class="{ 'dropdown-header__select__disabled': disabled }">
        {{ title }}
      </p>
    </div>
    <div
      v-if="!disabled"
      class="dropdown-header__arrow-button"
      :class="{
        'dropdown-header__arrow-button__rotated': !isOpen,
        'dropdown-header__arrow-button__disabled': disabled
      }"
      @click="onClickArrow"
    >
      <IconChevronUp class="dropdown-header__arrow-button__icon" />
    </div>
  </div>
</template>
<script lang="ts" setup>
import { ref, toRefs, watch } from 'vue'
import IconChevronUp from '@/design-system/assets/icons/IconChevronUp.vue'
import IconSearch from '@/design-system/assets/icons/IconSearch.vue'
interface HEADER_INPUT_PROPS {
  hasSearch?: boolean
  isOpen: boolean
  searchType?: string
  title: string
  value: string
  disabled?: boolean
}
interface EMITS {
  (e: 'change', value: string): void
  (e: 'focus'): void
  (e: 'toggle'): void
}

const props = defineProps<HEADER_INPUT_PROPS>()
const emits = defineEmits<EMITS>()
const { hasSearch, isOpen, searchType, title, value } = toRefs(props)
const inputRef = ref<HTMLInputElement | null>(null)
const onClickArrow = () => {
  if (hasSearch?.value) emits('toggle')
}
const onClickHeader = () => {
  if (!hasSearch?.value) emits('toggle')
}
const onClickInput = () => {
  if (!isOpen.value) emits('toggle')
}
const onChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  emits('change', target.value)
}
watch(isOpen, (newValue) => {
  if (!newValue) inputRef.value?.blur()
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
.dropdown-header {
  align-items: center;
  background-color: variables.$color-pure-white;
  border-radius: variables.$border-radius-medium;
  border: variables.$border-width-xsmall solid variables.$color-greyscale-secondary;
  box-shadow: none;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  gap: variables.$gap-medium;
  height: variables.$dropdown-height-medium;
  padding: 0 variables.$padding-xlarge;
  &__open {
    background-color: variables.$color-greyscale-background;
  }
  &__arrow-button {
    cursor: pointer;
    margin-left: auto;
    padding: variables.$padding-medium;
    &__icon {
      color: variables.$color-greyscale-secondary;
      position: relative;
      width: variables.$button-icon-xsmall;
    }
    &__rotated {
      transform: rotate(-180deg);
    }
    &__disabled {
      cursor: not-allowed;
    }
  }
  &__disabled {
    background-color: variables.$color-greyscale-background;
    border: variables.$border-width-xsmall solid variables.$color-greyscale-secondary;
    cursor: not-allowed;
    &::placeholder {
      color: variables.$color-text-secondary !important;
    }
  }
  &__error {
    border: variables.$border-width-xsmall solid variables.$color-error;
    &:focus {
      box-shadow: variables.$button-outline-focus;
    }
  }
  &__focus {
    border: variables.$border-width-xsmall solid variables.$color-primary;
    box-shadow: variables.$button-outline-focus;
  }
  &:hover:not(.dropdown-header__disabled) {
    border: variables.$border-width-xsmall solid variables.$color-primary;
    box-shadow: variables.$button-outline-focus;
  }
  &__icon {
    color: variables.$color-greyscale-content;
    min-width: 24px;
  }
  &__input,
  &__input::placeholder {
    border: none;
    color: variables.$color-text-primary;
    @include font-mixins.desktop-bold-paragraph;
    opacity: 1; // Necessary for Firefox placeholder text
    outline: none;
    padding: variables.$padding-xsmall;
    width: 90%;
  }
  &__select {
    @include font-mixins.desktop-bold-paragraph;
    color: variables.$color-greyscale-content;
    margin: 12px 0;
    &__disabled {
      color: variables.$color-greyscale-secondary;
    }
  }
  &__success {
    border: variables.$border-width-xsmall solid variables.$color-success;
  }
}
</style>
